import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { toast } from 'react-hot-toast';
import {
  ArrowPathIcon,
  ArrowTopRightOnSquareIcon,
  BuildingOfficeIcon,
  CalendarDaysIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ClipboardDocumentIcon,
  Cog6ToothIcon,
  EnvelopeIcon,
  PlusIcon,
  TruckIcon,
} from '@heroicons/react/24/outline';
import PageLayout from '../../components/PageLayout';
import LoadingSpinner from '../../components/LoadingSpinner';
import EntityDrawer from '../../components/ui/EntityDrawer';
import AccountTypeahead, {
  AccountTypeaheadOption,
} from '../../components/account/AccountTypeahead';
import CustomerTypeahead, {
  CustomerTypeaheadOption,
} from '../../components/customer/CustomerTypeahead';
import QuoteTermsEditor, {
  QuoteTermsShape,
  QuoteTermsSummary,
  mergeQuoteTerms,
} from '../../components/quotes/QuoteTermsEditor';
import { adminAPI } from '../../services/api';
import TripDetailsEditor from '../../components/reservations/TripDetailsEditor';
import { TripDetails, changedTripFields } from '../../types/trip-details';

type QuoteRequirementForm = {
  id?: string;
  vehicleType: string;
  vehicleLabel?: string;
  quantity: number;
  passengerCapacityRequired?: number | '';
  rateType: 'hourly' | 'daily' | 'fixed';
  hours?: number | '';
  days?: number | '';
  minimumHours?: number | '';
  unitRate?: number | '';
};

type QuoteAddressForm = {
  name: string;
  line1: string;
  line2: string;
  city: string;
  state: string;
  postalCode: string;
  notes: string;
};

type QuoteStopForm = QuoteAddressForm & {
  id: string;
};

type QuoteForm = {
  localCustomerId: string | null;
  accountId: string | null;
  customerReference: string | null;
  customerEmail: string;
  customerName: string;
  eventType: string;
  eventName: string;
  eventDate: string;
  startDate: string;
  endDate: string;
  pickupLocation: string;
  dropoffLocation: string;
  pickupAddress: QuoteAddressForm;
  dropoffAddress: QuoteAddressForm;
  stops: QuoteStopForm[];
  vehicleRequirements: QuoteRequirementForm[];
  termsOverride: QuoteTermsShape | null;
};

type QuoteVehicleRecord = {
  vehicle_type?: string | null;
  status?: string | null;
  hourly_rate?: string | number | null;
};

type VehicleCategoryPricing = {
  totalCount: number;
  availableCount: number;
  maxHourlyRate: number;
};

const EVENT_TYPES = [
  'general',
  'wedding',
  'charter',
  'corporate_event',
  'airport_transfer',
  'conference_shuttle',
  'prom_homecoming',
  'sports_vip',
];

const VEHICLE_TYPES = [
  'sedan',
  'suv',
  'van',
  'sprinter',
  'minibus',
  'motorcoach',
  'truck',
];

const emptyRequirement = (): QuoteRequirementForm => ({
  vehicleType: 'sprinter',
  vehicleLabel: '',
  quantity: 1,
  passengerCapacityRequired: '',
  rateType: 'hourly',
  hours: 4,
  days: '',
  minimumHours: '',
  unitRate: '',
});

const emptyAddress = (): QuoteAddressForm => ({
  name: '',
  line1: '',
  line2: '',
  city: '',
  state: '',
  postalCode: '',
  notes: '',
});

const emptyStop = (): QuoteStopForm => ({
  id: `stop_${Date.now()}_${Math.random().toString(16).slice(2)}`,
  ...emptyAddress(),
});

const emptyForm = (): QuoteForm => ({
  localCustomerId: null,
  accountId: null,
  customerReference: null,
  customerEmail: '',
  customerName: '',
  eventType: 'general',
  eventName: '',
  eventDate: '',
  startDate: '',
  endDate: '',
  pickupLocation: '',
  dropoffLocation: '',
  pickupAddress: emptyAddress(),
  dropoffAddress: emptyAddress(),
  stops: [],
  vehicleRequirements: [emptyRequirement()],
  termsOverride: null,
});

function formatMoney(value?: string | number | null) {
  const amount = Number(value || 0);
  return `$${(Number.isFinite(amount) ? amount : 0).toFixed(2)}`;
}

function formatRate(
  value?: string | number | null,
  rateType: QuoteRequirementForm['rateType'] = 'hourly'
) {
  if (value == null || value === '') return 'category pricing';
  const label =
    rateType === 'daily' ? 'day' : rateType === 'fixed' ? 'fixed' : 'hr';
  return `${formatMoney(value)}/${label}`;
}

function formatDate(value?: string | null) {
  if (!value) return 'N/A';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 'N/A' : date.toLocaleString();
}

function labelize(value?: string | null) {
  return (value || 'N/A')
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (match) => match.toUpperCase());
}

function normalizeAddress(
  value?: Record<string, any> | null,
  fallbackLocation = ''
): QuoteAddressForm {
  if (value && typeof value === 'object') {
    return {
      name: String(value.name || ''),
      line1: String(value.line1 || value.address_line1 || ''),
      line2: String(value.line2 || value.address_line2 || ''),
      city: String(value.city || ''),
      state: String(value.state || ''),
      postalCode: String(value.postalCode || value.postal_code || ''),
      notes: String(value.notes || ''),
    };
  }

  return {
    ...emptyAddress(),
    line1: fallbackLocation || '',
  };
}

function formatAddress(address: QuoteAddressForm) {
  return [
    address.name,
    address.line1,
    address.line2,
    [address.city, address.state, address.postalCode]
      .filter(Boolean)
      .join(', '),
  ]
    .filter((part) => part && part.trim())
    .join(' · ');
}

function serializeAddress(address: QuoteAddressForm) {
  return {
    name: address.name || null,
    line1: address.line1 || null,
    line2: address.line2 || null,
    city: address.city || null,
    state: address.state || null,
    postal_code: address.postalCode || null,
    notes: address.notes || null,
    formatted: formatAddress(address) || null,
  };
}

function normalizeStops(value?: any[]): QuoteStopForm[] {
  if (!Array.isArray(value)) return [];

  return value.map((stop, index) => ({
    id: String(stop.id || `stop_${index + 1}`),
    ...normalizeAddress(
      stop.address || stop,
      stop.formatted_location || stop.location || ''
    ),
  }));
}

function statusClass(status?: string | null) {
  switch ((status || '').toLowerCase()) {
    case 'draft':
      return 'bg-surface-container-high text-on-surface';
    case 'sent':
      return 'bg-blue-100 text-blue-800';
    case 'accepted':
      return 'bg-green-100 text-green-800';
    case 'declined':
    case 'expired':
      return 'bg-red-100 text-red-800';
    case 'converted':
      return 'bg-purple-100 text-purple-800';
    default:
      return 'bg-surface-container-low text-on-surface';
  }
}

function getQuoteLifecycleLabel(quote: any) {
  if (quote?.lifecycleLabel) return quote.lifecycleLabel;
  const status = String(quote?.status || '').toLowerCase();
  const paymentRequired = Boolean(
    quote?.paymentRequired || Number(quote?.depositDue || 0) > 0
  );
  const depositStatus = String(
    quote?.depositPayment?.status || quote?.depositPaymentStatus || ''
  ).toLowerCase();
  const hasDepositLink = Boolean(
    quote?.depositPaymentUrl || quote?.depositPayment?.publicUrl
  );

  if (paymentRequired && depositStatus === 'paid') return 'Deposit paid';
  if (
    paymentRequired &&
    hasDepositLink &&
    ['accepted', 'converted'].includes(status)
  ) {
    return 'Deposit due';
  }
  return labelize(status);
}

function getLastDeliveryLog(quote: any) {
  const log = Array.isArray(quote?.emailDeliveryLog)
    ? quote.emailDeliveryLog
    : [];
  return log.length ? log[log.length - 1] : null;
}

function getDepositStatusLabel(quote: any) {
  const depositDue = Number(quote?.depositDue || 0);
  const paymentRequired = Boolean(quote?.paymentRequired || depositDue > 0);
  const depositStatus = String(
    quote?.depositPayment?.status || quote?.depositPaymentStatus || ''
  ).toLowerCase();
  const hasDepositLink = Boolean(
    quote?.depositPaymentUrl || quote?.depositPayment?.publicUrl
  );

  if (!paymentRequired) return 'Not required';
  if (depositStatus === 'paid') return 'Paid';
  if (depositStatus === 'pending_payment') return 'Due';
  if (hasDepositLink) return 'Link ready';
  return 'Not created';
}

function depositStatusClass(quote: any) {
  const label = getDepositStatusLabel(quote).toLowerCase();
  if (label === 'paid') return 'bg-green-100 text-green-800';
  if (label === 'due' || label === 'link ready')
    return 'bg-yellow-100 text-yellow-800';
  if (label === 'not required')
    return 'bg-surface-container-high text-on-surface';
  return 'bg-red-100 text-red-800';
}

function toInputDateTime(value?: string | null) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const offset = date.getTimezoneOffset();
  const local = new Date(date.getTime() - offset * 60_000);
  return local.toISOString().slice(0, 16);
}

function normalizeRequirement(requirement: any): QuoteRequirementForm {
  return {
    id: requirement.id,
    vehicleType:
      requirement.vehicleType || requirement.vehicle_type || 'sprinter',
    vehicleLabel: requirement.vehicleLabel || requirement.vehicle_label || '',
    quantity: Number(requirement.quantity || 1),
    passengerCapacityRequired:
      requirement.passengerCapacityRequired ??
      requirement.passenger_capacity_required ??
      '',
    rateType: requirement.rateType || requirement.rate_type || 'hourly',
    hours: requirement.hours ?? '',
    days: requirement.days ?? '',
    minimumHours: requirement.minimumHours ?? requirement.minimum_hours ?? '',
    unitRate: requirement.unitRate ?? requirement.unit_rate ?? '',
  };
}

function buildPayload(form: QuoteForm) {
  const pickupLocation =
    formatAddress(form.pickupAddress) || form.pickupLocation || null;
  const dropoffLocation =
    formatAddress(form.dropoffAddress) || form.dropoffLocation || null;

  return {
    localCustomerId: form.localCustomerId,
    accountId: form.accountId || null,
    customerReference: form.customerReference || null,
    customerEmail: form.customerEmail || null,
    customerName: form.customerName || null,
    eventType: form.eventType,
    eventName: form.eventName || null,
    eventDate: form.eventDate ? new Date(form.eventDate).toISOString() : null,
    tripDetails: {
      startDate: form.startDate ? new Date(form.startDate).toISOString() : null,
      endDate: form.endDate ? new Date(form.endDate).toISOString() : null,
      pickup_location: pickupLocation,
      dropoff_location: dropoffLocation,
      pickup_address: serializeAddress(form.pickupAddress),
      dropoff_address: serializeAddress(form.dropoffAddress),
      stops: form.stops.map((stop, index) => ({
        id: stop.id,
        label: stop.name || formatAddress(stop) || `Stop ${index + 1}`,
        kind: 'stop',
        sequence: index + 1,
        formatted_location: formatAddress(stop) || null,
        address: serializeAddress(stop),
      })),
    },
    vehicleRequirements: form.vehicleRequirements.map((requirement) => ({
      id: requirement.id,
      vehicleType: requirement.vehicleType,
      vehicleLabel: requirement.vehicleLabel || null,
      quantity: Number(requirement.quantity || 1),
      passengerCapacityRequired:
        requirement.passengerCapacityRequired === ''
          ? null
          : Number(requirement.passengerCapacityRequired),
      rateType: requirement.rateType,
      hours: requirement.hours === '' ? null : Number(requirement.hours),
      days: requirement.days === '' ? null : Number(requirement.days),
      minimumHours:
        requirement.minimumHours === ''
          ? null
          : Number(requirement.minimumHours),
      unitRate:
        requirement.unitRate === '' ? null : Number(requirement.unitRate),
    })),
    termsOverride: form.termsOverride || undefined,
  };
}

function buildVehicleCategoryPricing(vehicles: QuoteVehicleRecord[]) {
  return vehicles.reduce<Record<string, VehicleCategoryPricing>>(
    (acc, vehicle) => {
      const type = String(vehicle.vehicle_type || '').toLowerCase();
      if (!type) return acc;

      const current = acc[type] || {
        totalCount: 0,
        availableCount: 0,
        maxHourlyRate: 0,
      };
      const hourlyRate = Number(vehicle.hourly_rate || 0);
      current.totalCount += 1;
      if (vehicle.status === 'available') {
        current.availableCount += 1;
        if (Number.isFinite(hourlyRate)) {
          current.maxHourlyRate = Math.max(current.maxHourlyRate, hourlyRate);
        }
      }
      acc[type] = current;
      return acc;
    },
    {}
  );
}

function getCategoryUnitRate(
  category: VehicleCategoryPricing | undefined,
  rateType: QuoteRequirementForm['rateType']
) {
  const hourlyRate = Number(category?.maxHourlyRate || 0);
  if (!Number.isFinite(hourlyRate) || hourlyRate <= 0) return 0;
  return rateType === 'daily' ? hourlyRate * 24 : hourlyRate;
}

function quoteMoney(value: number) {
  return (Number.isFinite(value) ? value : 0).toFixed(2);
}

function toCentsFromDollars(value: number) {
  return Math.round((Number.isFinite(value) ? value : 0) * 100);
}

function fromCentsToDollars(value: number) {
  return value / 100;
}

function positiveNumber(value: unknown, fallback: number) {
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric > 0 ? numeric : fallback;
}

function deriveHoursFromForm(form: QuoteForm) {
  if (!form.startDate || !form.endDate) return null;
  const start = new Date(form.startDate);
  const end = new Date(form.endDate);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null;
  const hours = Math.ceil((end.getTime() - start.getTime()) / 3_600_000);
  return Math.max(1, hours);
}

function calculateLocalPricingPreview(
  form: QuoteForm,
  terms: QuoteTermsShape,
  vehicleCategoryPricing: Record<string, VehicleCategoryPricing>
) {
  const derivedHours = deriveHoursFromForm(form);
  let subtotal = 0;

  const requirements = form.vehicleRequirements.map((requirement, index) => {
    const rateType = requirement.rateType || 'hourly';
    const category =
      vehicleCategoryPricing[String(requirement.vehicleType || '').toLowerCase()];
    const categoryUnitRate = getCategoryUnitRate(category, rateType);
    const manualRate = Number(requirement.unitRate);
    const unitRate =
      requirement.unitRate !== '' && Number.isFinite(manualRate) && manualRate > 0
        ? manualRate
        : categoryUnitRate;
    const minimumHours = positiveNumber(
      requirement.minimumHours || terms.minimum_hours,
      1
    );
    const requestedHours = positiveNumber(
      requirement.hours || derivedHours || minimumHours,
      minimumHours
    );
    const requestedDays = positiveNumber(
      requirement.days || Math.ceil(requestedHours / 24),
      1
    );
    const quantity = Math.max(1, Number(requirement.quantity || 1));
    const billableUnits =
      rateType === 'daily'
        ? Math.max(1, requestedDays)
        : rateType === 'fixed'
        ? 1
        : Math.max(minimumHours, requestedHours);
    const lineSubtotal = quantity * unitRate * billableUnits;
    subtotal += lineSubtotal;

    return {
      id: requirement.id || `req_${index + 1}`,
      vehicle_type: requirement.vehicleType,
      quantity,
      rate_type: rateType,
      unit_rate: unitRate,
      billable_units: billableUnits,
      subtotal: Number(lineSubtotal.toFixed(2)),
    };
  });

  const subtotalCents = toCentsFromDollars(subtotal);
  const serviceChargeCents = Math.round(
    (subtotalCents * Number(terms.service_charge_percent || 0)) / 100
  );
  const gratuityCents = Math.round(
    (subtotalCents * Number(terms.gratuity_percent || 0)) / 100
  );
  const taxableBaseCents = subtotalCents + serviceChargeCents + gratuityCents;
  const taxCents = Math.round(taxableBaseCents * Number(terms.tax_rate || 0));
  const totalCents = taxableBaseCents + taxCents;
  const deposit = terms.deposit || { type: 'none', value: 0 };
  const depositDueCents =
    deposit.type === 'none'
      ? 0
      : deposit.type === 'fixed'
      ? Math.min(totalCents, toCentsFromDollars(Number(deposit.value || 0)))
      : Math.round((totalCents * Number(deposit.value || 0)) / 100);

  return {
    requirements,
    subtotal: quoteMoney(fromCentsToDollars(subtotalCents)),
    serviceCharge: quoteMoney(fromCentsToDollars(serviceChargeCents)),
    gratuity: quoteMoney(fromCentsToDollars(gratuityCents)),
    tax: quoteMoney(fromCentsToDollars(taxCents)),
    total: quoteMoney(fromCentsToDollars(totalCents)),
    depositDue: quoteMoney(fromCentsToDollars(depositDueCents)),
  };
}

function QuoteAccordionSection({
  title,
  description,
  summary,
  actions,
  defaultOpen = true,
  children,
}: {
  title: string;
  description?: string;
  summary?: React.ReactNode;
  actions?: React.ReactNode;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  const [expanded, setExpanded] = useState(defaultOpen);

  return (
    <section className="overflow-hidden rounded-card border border-outline/70 bg-surface-container-lowest">
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-outline/60 px-4 py-3 sm:px-5">
        <button
          type="button"
          className="flex min-w-0 flex-1 items-start gap-3 text-left"
          onClick={() => setExpanded((current) => !current)}
          aria-expanded={expanded}
        >
          <span className="mt-0.5 inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full border border-outline/60 bg-surface-container-low text-on-surface-variant">
            {expanded ? (
              <ChevronDownIcon className="h-4 w-4" />
            ) : (
              <ChevronRightIcon className="h-4 w-4" />
            )}
          </span>
          <span className="min-w-0">
            <span className="block text-sm font-semibold text-on-surface">
              {title}
            </span>
            {description ? (
              <span className="mt-1 block text-sm text-on-surface-variant">
                {description}
              </span>
            ) : null}
            {summary && !expanded ? (
              <span className="mt-1 block truncate text-xs text-on-surface-variant">
                {summary}
              </span>
            ) : null}
          </span>
        </button>
        {actions ? (
          <div className="flex flex-shrink-0 items-center gap-2">{actions}</div>
        ) : null}
      </div>
      {expanded ? <div className="p-4 sm:p-5">{children}</div> : null}
    </section>
  );
}

function AddressFields({
  address,
  onChange,
  prefix,
}: {
  address: QuoteAddressForm;
  onChange: (patch: Partial<QuoteAddressForm>) => void;
  prefix: string;
}) {
  return (
    <div className="grid min-w-0 gap-3 sm:grid-cols-2 xl:grid-cols-6">
      <label className="min-w-0 xl:col-span-2">
        <span className="text-xs font-medium text-on-surface-variant">
          Place name
        </span>
        <input
          className="input-field mt-1"
          value={address.name}
          onChange={(event) => onChange({ name: event.target.value })}
          placeholder={`${prefix} place`}
        />
      </label>
      <label className="min-w-0 xl:col-span-4">
        <span className="text-xs font-medium text-on-surface-variant">
          Street address
        </span>
        <input
          className="input-field mt-1"
          value={address.line1}
          onChange={(event) => onChange({ line1: event.target.value })}
          placeholder="Street address"
        />
      </label>
      <label className="min-w-0 xl:col-span-2">
        <span className="text-xs font-medium text-on-surface-variant">
          Address 2
        </span>
        <input
          className="input-field mt-1"
          value={address.line2}
          onChange={(event) => onChange({ line2: event.target.value })}
          placeholder="Suite, terminal, dock..."
        />
      </label>
      <label className="min-w-0 xl:col-span-2">
        <span className="text-xs font-medium text-on-surface-variant">
          City
        </span>
        <input
          className="input-field mt-1"
          value={address.city}
          onChange={(event) => onChange({ city: event.target.value })}
          placeholder="San Antonio"
        />
      </label>
      <label className="min-w-0">
        <span className="text-xs font-medium text-on-surface-variant">
          State
        </span>
        <input
          className="input-field mt-1"
          value={address.state}
          onChange={(event) =>
            onChange({ state: event.target.value.toUpperCase() })
          }
          placeholder="TX"
        />
      </label>
      <label className="min-w-0">
        <span className="text-xs font-medium text-on-surface-variant">ZIP</span>
        <input
          className="input-field mt-1"
          value={address.postalCode}
          onChange={(event) => onChange({ postalCode: event.target.value })}
          placeholder="78205"
        />
      </label>
      <label className="min-w-0 sm:col-span-2 xl:col-span-6">
        <span className="text-xs font-medium text-on-surface-variant">
          Location notes
        </span>
        <input
          className="input-field mt-1"
          value={address.notes}
          onChange={(event) => onChange({ notes: event.target.value })}
          placeholder="Entrance, greeter, staging, parking, or timing notes"
        />
      </label>
    </div>
  );
}

export default function AdminQuotes() {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState({
    status: '',
    search: '',
    fromDate: '',
    toDate: '',
    page: 1,
    limit: 20,
  });
  const [drawerOpen, setDrawerOpen] = useState(() => Boolean(new URLSearchParams(window.location.search).get('quote')));
  const [selectedQuoteId, setSelectedQuoteId] = useState<string | null>(() => new URLSearchParams(window.location.search).get('quote'));

  const { data, isLoading, isFetching, error } = useQuery(
    ['admin-quotes', filters],
    async () => {
      const response = await adminAPI.getQuotes({
        status: filters.status || undefined,
        search: filters.search || undefined,
        fromDate: filters.fromDate || undefined,
        toDate: filters.toDate || undefined,
        page: filters.page,
        limit: filters.limit,
      });
      return response.data;
    },
    { keepPreviousData: true }
  );

  const quotes = useMemo(
    () => (Array.isArray(data?.data) ? data.data : []),
    [data?.data]
  );
  const pagination = data?.pagination;
  const counts = useMemo(() => {
    const next = { draft: 0, sent: 0, accepted: 0, converted: 0 };
    quotes.forEach((quote: any) => {
      if (quote.status in next) next[quote.status as keyof typeof next] += 1;
    });
    return next;
  }, [quotes]);

  return (
    <PageLayout className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-on-surface">Quotes</h1>
          <p className="mt-1 text-on-surface-variant">
            Create customer-ready quotes from vehicle requirements, terms, and
            availability.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isFetching ? (
            <span className="rounded-pill bg-surface-container-high px-3 py-1 text-xs text-on-surface-variant">
              Refreshing...
            </span>
          ) : null}
          <button
            type="button"
            className="btn-primary inline-flex items-center gap-2"
            onClick={() => {
              setSelectedQuoteId(null);
              setDrawerOpen(true);
            }}
          >
            <PlusIcon className="h-4 w-4" />
            New Quote
          </button>
        </div>
      </div>

      {error ? (
        <div className="rounded-card border border-error/40 bg-error-container/20 px-4 py-3 text-sm text-on-surface">
          Failed to load quotes.{' '}
          {(error as any)?.response?.data?.message ||
            (error as any)?.message ||
            'Unknown error'}
        </div>
      ) : null}

      <section className="admin-subpanel rounded-card p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-sm font-semibold text-on-surface">
              Quote setup
            </h2>
            <p className="mt-1 text-sm text-on-surface-variant">
              Manage tenant defaults, account terms, and vehicle rates used when
              quotes are priced.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Link
              className="btn-secondary inline-flex items-center gap-2"
              to="/admin/settings?tab=quoteTerms"
            >
              <Cog6ToothIcon className="h-4 w-4" />
              Quote Defaults
            </Link>
            <Link
              className="btn-secondary inline-flex items-center gap-2"
              to="/admin/accounts"
            >
              <BuildingOfficeIcon className="h-4 w-4" />
              Account Terms
            </Link>
            <Link
              className="btn-secondary inline-flex items-center gap-2"
              to="/admin/vehicles"
            >
              <TruckIcon className="h-4 w-4" />
              Vehicles
            </Link>
          </div>
        </div>
      </section>

      <div className="grid gap-4 md:grid-cols-4">
        {(['draft', 'sent', 'accepted', 'converted'] as const).map((status) => (
          <div key={status} className="admin-subpanel rounded-card p-4">
            <p className="text-sm text-on-surface-variant">
              {labelize(status)}
            </p>
            <p className="mt-1 text-2xl font-semibold text-on-surface">
              {counts[status]}
            </p>
          </div>
        ))}
      </div>

      <div className="admin-subpanel rounded-card p-4">
        <div className="grid gap-4 md:grid-cols-5">
          <label>
            <span className="text-sm font-medium text-on-surface-variant">
              Status
            </span>
            <select
              className="input-field mt-1"
              value={filters.status}
              onChange={(event) =>
                setFilters({ ...filters, status: event.target.value, page: 1 })
              }
            >
              <option value="">All statuses</option>
              {[
                'draft',
                'sent',
                'accepted',
                'declined',
                'expired',
                'converted',
              ].map((status) => (
                <option key={status} value={status}>
                  {labelize(status)}
                </option>
              ))}
            </select>
          </label>
          <label className="md:col-span-2">
            <span className="text-sm font-medium text-on-surface-variant">
              Search
            </span>
            <input
              className="input-field mt-1"
              value={filters.search}
              onChange={(event) =>
                setFilters({ ...filters, search: event.target.value, page: 1 })
              }
              placeholder="Quote, event, customer..."
            />
          </label>
          <label>
            <span className="text-sm font-medium text-on-surface-variant">
              From
            </span>
            <input
              type="date"
              className="input-field mt-1"
              value={filters.fromDate}
              onChange={(event) =>
                setFilters({
                  ...filters,
                  fromDate: event.target.value,
                  page: 1,
                })
              }
            />
          </label>
          <label>
            <span className="text-sm font-medium text-on-surface-variant">
              To
            </span>
            <input
              type="date"
              className="input-field mt-1"
              value={filters.toDate}
              onChange={(event) =>
                setFilters({ ...filters, toDate: event.target.value, page: 1 })
              }
            />
          </label>
        </div>
      </div>

      <section className="admin-subpanel rounded-card p-4">
        {isLoading ? (
          <LoadingSpinner className="py-20" />
        ) : quotes.length > 0 ? (
          <div className="overflow-x-auto rounded-card border border-outline/60">
            <table className="min-w-full divide-y divide-outline">
              <thead className="bg-surface-container-low">
                <tr>
                  {[
                    'Quote',
                    'Customer',
                    'Event',
                    'Vehicles',
                    'Total',
                    'Deposit',
                    'Status',
                  ].map((heading) => (
                    <th
                      key={heading}
                      className="px-6 py-3 text-left text-xs font-medium uppercase text-on-surface-variant"
                    >
                      {heading}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-outline bg-surface-container-lowest">
                {quotes.map((quote: any) => (
                  <tr
                    key={quote.id}
                    className="cursor-pointer hover:bg-surface-container-low"
                    onClick={() => {
                      setSelectedQuoteId(quote.id);
                      setDrawerOpen(true);
                    }}
                  >
                    <td className="px-6 py-4">
                      <p className="font-semibold text-on-surface">
                        {quote.quoteNumber}
                      </p>
                      <p className="mt-1 text-xs text-on-surface-variant">
                        Updated {formatDate(quote.updatedAt)}
                      </p>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-sm font-medium text-on-surface">
                        {quote.customerName ||
                          quote.customerEmail ||
                          'No customer'}
                      </p>
                      <p className="text-xs text-on-surface-variant">
                        {quote.account?.name ||
                          quote.customerEmail ||
                          'No account'}
                      </p>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-sm font-medium text-on-surface">
                        {quote.eventName || labelize(quote.eventType)}
                      </p>
                      <p className="text-xs text-on-surface-variant">
                        {formatDate(quote.eventDate)}
                      </p>
                    </td>
                    <td className="px-6 py-4 text-sm text-on-surface">
                      {(quote.vehicleRequirements || [])
                        .map(
                          (requirement: any) =>
                            `${requirement.quantity} ${
                              requirement.vehicle_label ||
                              requirement.vehicle_type
                            }`
                        )
                        .join(', ') || 'No requirements'}
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-sm font-semibold text-on-surface">
                        {formatMoney(quote.total)}
                      </p>
                      <p className="text-xs text-on-surface-variant">
                        Deposit {formatMoney(quote.depositDue)}
                      </p>
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`rounded-pill whitespace-nowrap px-2 py-1 text-xs font-semibold ${depositStatusClass(
                          quote
                        )}`}
                      >
                        {getDepositStatusLabel(quote)}
                      </span>
                      {quote.depositPayment?.invoiceNumber ||
                      quote.depositInvoiceNumber ? (
                        <p className="mt-1 text-xs text-on-surface-variant">
                          {quote.depositPayment?.invoiceNumber ||
                            quote.depositInvoiceNumber}
                        </p>
                      ) : null}
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`rounded-pill whitespace-nowrap px-2 py-1 text-xs font-semibold ${statusClass(
                          quote.status
                        )}`}
                      >
                        {getQuoteLifecycleLabel(quote)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="py-16 text-center">
            <CalendarDaysIcon className="mx-auto h-12 w-12 text-on-surface-variant" />
            <h3 className="mt-3 text-lg font-semibold text-on-surface">
              No quotes found
            </h3>
            <p className="mt-1 text-sm text-on-surface-variant">
              Create the first quote to begin the reservation workflow.
            </p>
          </div>
        )}

        {pagination && pagination.totalPages > 1 ? (
          <div className="mt-4 flex justify-end gap-2">
            <button
              className="btn-secondary"
              disabled={filters.page <= 1}
              onClick={() => setFilters({ ...filters, page: filters.page - 1 })}
            >
              Previous
            </button>
            <button
              className="btn-secondary"
              disabled={filters.page >= pagination.totalPages}
              onClick={() => setFilters({ ...filters, page: filters.page + 1 })}
            >
              Next
            </button>
          </div>
        ) : null}
      </section>

      <QuoteDrawer
        open={drawerOpen}
        quoteId={selectedQuoteId}
        onClose={() => setDrawerOpen(false)}
        onUpdated={() => queryClient.invalidateQueries('admin-quotes')}
      />
    </PageLayout>
  );
}

function QuoteDrawer({
  open,
  quoteId,
  onClose,
  onUpdated,
}: {
  open: boolean;
  quoteId: string | null;
  onClose: () => void;
  onUpdated: () => void;
}) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<QuoteForm>(emptyForm());
  const [baselineForm, setBaselineForm] = useState<QuoteForm | null>(null);
  const [tripPatch, setTripPatch] = useState<Partial<TripDetails>>({});
  const [changeReason, setChangeReason] = useState('');
  const [selectedAccount, setSelectedAccount] =
    useState<AccountTypeaheadOption | null>(null);
  const [selectedCustomer, setSelectedCustomer] =
    useState<CustomerTypeaheadOption | null>(null);
  const [publicUrl, setPublicUrl] = useState<string | null>(null);
  const [declinePanelOpen, setDeclinePanelOpen] = useState(false);
  const [declineReason, setDeclineReason] = useState('');

  const { data: vehiclePricingData } = useQuery(
    ['quote-vehicle-category-pricing'],
    async () => {
      const response = await adminAPI.getAllVehicles?.();
      return Array.isArray(response?.data)
        ? (response.data as QuoteVehicleRecord[])
        : [];
    },
    { enabled: open, staleTime: 60_000 }
  );

  const { data: quote, isLoading } = useQuery(
    ['admin-quote', quoteId],
    async () => {
      if (!quoteId) return null;
      const response = await adminAPI.getQuote(quoteId);
      return response.data;
    },
    { enabled: open && Boolean(quoteId), retry: false }
  );

  useEffect(() => {
    if (!open) return;
    setTripPatch({}); setChangeReason('');
    if (!quoteId) {
      setForm(emptyForm());
      setBaselineForm(null);
      setSelectedAccount(null);
      setSelectedCustomer(null);
      setPublicUrl(null);
      return;
    }
    if (quote) {
      const nextForm: QuoteForm = {
        localCustomerId: quote.tripDetails?.local_customer_id || null,
        accountId: quote.accountId || null,
        customerReference: quote.customerReference || null,
        customerEmail: quote.customerEmail || '',
        customerName: quote.customerName || '',
        eventType: quote.eventType || 'general',
        eventName: quote.eventName || '',
        eventDate: toInputDateTime(quote.eventDate),
        startDate: toInputDateTime(
          quote.tripDetails?.startDate || quote.tripDetails?.start_date
        ),
        endDate: toInputDateTime(
          quote.tripDetails?.endDate || quote.tripDetails?.end_date
        ),
        pickupLocation: quote.tripDetails?.pickup_location || '',
        dropoffLocation: quote.tripDetails?.dropoff_location || '',
        pickupAddress: normalizeAddress(
          quote.tripDetails?.pickup_address,
          quote.tripDetails?.pickup_location || ''
        ),
        dropoffAddress: normalizeAddress(
          quote.tripDetails?.dropoff_address,
          quote.tripDetails?.dropoff_location || ''
        ),
        stops: normalizeStops(quote.tripDetails?.stops),
        vehicleRequirements:
          Array.isArray(quote.vehicleRequirements) &&
          quote.vehicleRequirements.length > 0
            ? quote.vehicleRequirements.map(normalizeRequirement)
            : [emptyRequirement()],
        termsOverride: quote.termsSnapshot || null,
      };
      setForm(nextForm); setBaselineForm(nextForm);
      setSelectedAccount(
        quote.account
          ? {
              id: quote.account.id,
              label: quote.account.name,
              description: 'Attached account',
            }
          : null
      );
      setSelectedCustomer(
        quote.customerEmail
          ? {
              id: quote.customerReference || quote.customerEmail,
              label: quote.customerName || quote.customerEmail,
              email: quote.customerEmail,
              description: quote.customerEmail,
            }
          : null
      );
      setPublicUrl(null);
    }
  }, [open, quoteId, quote]);

  const saveMutation = useMutation(
    async () => {
      const fullPayload = buildPayload(form);
      let payload: Record<string, any> = fullPayload;
      if (quoteId && baselineForm) {
        const baseline = buildPayload(baselineForm);
        payload = Object.fromEntries(Object.entries(fullPayload).filter(([key, value]) => key !== 'tripDetails' && JSON.stringify(value) !== JSON.stringify((baseline as any)[key])));
        const detailPatch = changedTripFields(baseline.tripDetails, fullPayload.tripDetails);
        Object.assign(detailPatch, changedTripFields(quote?.tripDetails || {}, tripPatch));
        if (Object.keys(detailPatch).length) payload.tripDetails = detailPatch;
        payload.expectedUpdatedAt = quote?.updatedAt;
        payload.changeReason = changeReason.trim() || null;
        if ((quote?.sentAt || locked) && !payload.changeReason) throw new Error('A change reason is required');
      } else payload.tripDetails = { ...fullPayload.tripDetails, ...tripPatch };
      return quoteId
        ? adminAPI.updateQuote(quoteId, payload)
        : adminAPI.createQuote(payload);
    },
    {
      onSuccess: async (response) => {
        toast.success(quoteId ? 'Quote updated' : 'Quote created');
        await Promise.all([
          queryClient.invalidateQueries('admin-quotes'),
          queryClient.invalidateQueries(['admin-quote', quoteId]),
        ]);
        onUpdated();
        if (!quoteId && response?.data?.id) {
          setPublicUrl(null);
        }
      },
      onError: (error: any) => {
        toast.error(error?.response?.data?.message || 'Failed to save quote');
      },
    }
  );

  const actionMutation = useMutation(
    async (
      action:
        | 'link'
        | 'send'
        | 'recalculate'
        | 'accept'
        | 'decline'
        | 'convert'
        | 'revoke'
	    ) => {
	      if (!quoteId) throw new Error('Save the quote first');
	      const baseUrl =
	        typeof window !== 'undefined' ? window.location.origin : undefined;
	      if (action === 'link') return adminAPI.generateQuoteLink(quoteId, { baseUrl });
	      if (action === 'send') return adminAPI.sendQuote(quoteId, { baseUrl });
	      if (action === 'recalculate') return adminAPI.recalculateQuote(quoteId);
	      if (action === 'accept') return adminAPI.acceptQuote(quoteId, { baseUrl });
      if (action === 'decline')
        return adminAPI.declineQuote(quoteId, {
          reason: declineReason.trim() || undefined,
        });
      if (action === 'revoke') return adminAPI.revokeQuoteLink(quoteId);
      return adminAPI.convertQuoteToReservation(quoteId, {
        requireAccepted: true,
      });
    },
    {
      onSuccess: async (response, action) => {
        if (response?.data?.public_url) {
          setPublicUrl(response.data.public_url);
          if (action === 'link' && navigator.clipboard) {
            await navigator.clipboard
              .writeText(response.data.public_url)
              .catch(() => undefined);
          }
        }
        toast.success(
          action === 'link'
            ? 'Quote link generated'
            : action === 'send'
            ? 'Quote sent'
            : 'Quote updated'
        );
        await Promise.all([
          queryClient.invalidateQueries('admin-quotes'),
          quoteId
            ? queryClient.invalidateQueries(['admin-quote', quoteId])
            : Promise.resolve(),
        ]);
        if (action === 'decline') {
          setDeclinePanelOpen(false);
          setDeclineReason('');
        }
        onUpdated();
      },
      onError: (error: any) => {
        toast.error(error?.response?.data?.message || 'Quote action failed');
      },
    }
  );

  const pricing = quote?.pricingSnapshot;
  const locked = Boolean(quote?.depositInvoiceId || quote?.depositPaymentRequestId) || ['accepted', 'converted'].includes(
    String(quote?.status || '')
  );
  const vehicleCategoryPricing = useMemo(
    () => buildVehicleCategoryPricing(vehiclePricingData || []),
    [vehiclePricingData]
  );

  const updateRequirement = (
    index: number,
    patch: Partial<QuoteRequirementForm>
  ) => {
    setForm((current) => ({
      ...current,
      vehicleRequirements: current.vehicleRequirements.map((requirement, i) =>
        i === index ? { ...requirement, ...patch } : requirement
      ),
    }));
  };

  const updateAddress = (
    key: 'pickupAddress' | 'dropoffAddress',
    patch: Partial<QuoteAddressForm>
  ) => {
    setForm((current) => ({
      ...current,
      [key]: {
        ...current[key],
        ...patch,
      },
    }));
  };

  const updateStop = (stopId: string, patch: Partial<QuoteStopForm>) => {
    setForm((current) => ({
      ...current,
      stops: current.stops.map((stop) =>
        stop.id === stopId ? { ...stop, ...patch } : stop
      ),
    }));
  };

  const patchQuoteTerms = (patch: Partial<QuoteTermsShape>) => {
    setForm((current) => ({
      ...current,
      termsOverride: mergeQuoteTerms(
        patch,
        mergeQuoteTerms(current.termsOverride || quote?.termsSnapshot || null)
      ),
    }));
  };

  const pickupSummary =
    formatAddress(form.pickupAddress) || 'Pickup address not set';
  const dropoffSummary =
    formatAddress(form.dropoffAddress) || 'Dropoff address not set';
  const customerSummary =
    form.customerName || form.customerEmail || 'Customer not selected';
  const eventSummary = [
    labelize(form.eventType),
    form.eventName,
    form.eventDate ? formatDate(form.eventDate) : null,
  ]
    .filter(Boolean)
    .join(' · ');
  const effectiveQuoteTerms = mergeQuoteTerms(
    form.termsOverride || quote?.termsSnapshot || null
  );
  const localPricingPreview = useMemo(
    () =>
      calculateLocalPricingPreview(
        form,
        effectiveQuoteTerms,
        vehicleCategoryPricing
      ),
    [effectiveQuoteTerms, form, vehicleCategoryPricing]
  );
  const previewPricing = locked ? pricing : localPricingPreview;
  const pricingTotal = formatMoney(previewPricing?.total || quote?.total);
  const pricingDeposit = formatMoney(
    previewPricing?.depositDue || quote?.depositDue
  );
	  const readiness = quote?.readiness || {
	    blockingIssues: [],
	    warnings: [],
	  };
	  const allowedActions = quote?.allowedActions || {};
	  const canUseAction = (action: string, fallback = false) =>
	    Boolean(allowedActions[action] ?? fallback);
  const canEdit = true;
	  const depositPaymentUrl =
	    quote?.depositPaymentUrl || quote?.depositPayment?.publicUrl || null;
  const depositPaymentRequestId =
    quote?.depositPaymentRequestId || quote?.depositPayment?.requestId || null;
  const depositInvoiceNumber =
    quote?.depositInvoiceNumber || quote?.depositPayment?.invoiceNumber || null;
  const depositPaidAt = quote?.depositPayment?.paidAt || null;
	  const lastDelivery = getLastDeliveryLog(quote);
	  const convertedReservationAdminUrl = quote?.convertedReservationId
	    ? `/admin/reservations?search=${encodeURIComponent(
	        quote.quoteNumber || quote.convertedReservationId
	      )}`
	    : null;
	  const convertedReservationPublicUrl =
	    quote?.convertedReservationPublicUrl ||
	    quote?.tripDetails?.converted_reservation_public_url ||
	    null;

	  const copyDepositPaymentLink = async () => {
	    if (!depositPaymentUrl) return;
    await navigator.clipboard
      ?.writeText(depositPaymentUrl)
      .catch(() => undefined);
	    toast.success('Deposit payment link copied');
	  };

	  const copyReservationRecordLink = async () => {
	    if (!convertedReservationPublicUrl) return;
	    await navigator.clipboard
	      ?.writeText(convertedReservationPublicUrl)
	      .catch(() => undefined);
	    toast.success('Reservation record link copied');
	  };

  return (
    <EntityDrawer
      open={open}
      onClose={onClose}
      title={quoteId ? quote?.quoteNumber || 'Quote' : 'New Quote'}
      subtitle={
        quoteId
          ? `${getQuoteLifecycleLabel(quote)} quote`
          : 'Build from vehicle requirements'
      }
      widthClassName="max-w-6xl"
      headerActions={
        quoteId ? (
          <span
            className={`rounded-pill whitespace-nowrap px-2 py-1 text-xs font-semibold ${statusClass(
              quote?.status
            )}`}
          >
            {getQuoteLifecycleLabel(quote)}
          </span>
        ) : null
      }
      secondary={{
        open: declinePanelOpen,
        onClose: () => {
          setDeclinePanelOpen(false);
          setDeclineReason('');
        },
        title: 'Decline Quote',
        subtitle: quote?.quoteNumber || undefined,
        children: (
          <div className="space-y-4">
            <div className="rounded-card border border-outline/60 bg-surface-container-low p-4">
              <p className="text-sm font-semibold text-on-surface">
                Move this quote to declined
              </p>
              <p className="mt-1 text-sm text-on-surface-variant">
                Add an optional internal reason so the team understands why the customer is not moving forward.
              </p>
            </div>
            <label className="block">
              <span className="mb-1 block text-sm font-medium text-on-surface-variant">
                Decline reason
              </span>
              <textarea
                className="input-field min-h-[140px]"
                value={declineReason}
                onChange={(event) => setDeclineReason(event.target.value)}
                placeholder="Example: Customer chose a different date, budget changed, event cancelled..."
              />
            </label>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => {
                  setDeclinePanelOpen(false);
                  setDeclineReason('');
                }}
                disabled={actionMutation.isLoading}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-primary"
                onClick={() => actionMutation.mutate('decline')}
                disabled={actionMutation.isLoading}
              >
                Decline Quote
              </button>
            </div>
          </div>
        ),
      }}
		      footer={
	        <>
	          {quoteId && canUseAction('copyDepositLink') ? (
	            depositPaymentUrl ? (
	              <button
	                type="button"
                className="btn-secondary"
                onClick={copyDepositPaymentLink}
              >
                <ClipboardDocumentIcon className="mr-1 inline h-4 w-4" />
	                Copy Deposit Link
	              </button>
	            ) : null
	          ) : null}
	          {convertedReservationAdminUrl && canUseAction('openConvertedReservation') ? (
	            <Link className="btn-secondary" to={convertedReservationAdminUrl}>
	              <ArrowTopRightOnSquareIcon className="mr-1 inline h-4 w-4" />
	              Reservation
	            </Link>
	          ) : null}
	          {quoteId && canUseAction('generateLink') ? (
	            <button
	              type="button"
	              className="btn-secondary"
              disabled={actionMutation.isLoading}
              onClick={() => actionMutation.mutate('link')}
            >
              <ClipboardDocumentIcon className="mr-1 inline h-4 w-4" />
	              Copy Link
	            </button>
	          ) : null}
	          {quoteId && canUseAction('send') ? (
	            <button
	              type="button"
	              className="btn-secondary"
              disabled={actionMutation.isLoading}
              onClick={() => actionMutation.mutate('send')}
            >
              <EnvelopeIcon className="mr-1 inline h-4 w-4" />
	              Send
	            </button>
	          ) : null}
	          {quoteId && canUseAction('revokeLink') ? (
	            <button
	              type="button"
	              className="btn-secondary"
	              disabled={actionMutation.isLoading}
	              onClick={() => actionMutation.mutate('revoke')}
	            >
	              Revoke Link
	            </button>
	          ) : null}
	          {quoteId && canUseAction('decline') ? (
	            <button
		              type="button"
		              className="btn-secondary"
		              disabled={actionMutation.isLoading}
		              onClick={() => setDeclinePanelOpen(true)}
		            >
	              Decline
	            </button>
	          ) : null}
	          {quoteId && canUseAction('accept') ? (
	            <button
	              type="button"
	              className="btn-secondary"
              disabled={actionMutation.isLoading}
              onClick={() => actionMutation.mutate('accept')}
            >
	              Accept
	            </button>
	          ) : null}
	          {quoteId && canUseAction('convert') ? (
	            <button
	              type="button"
              className="btn-secondary"
              disabled={actionMutation.isLoading}
              onClick={() => actionMutation.mutate('convert')}
            >
	              Convert
	            </button>
	          ) : null}
	          {canEdit ? (
	            <button
	              type="button"
	              className="btn-primary"
	              disabled={saveMutation.isLoading}
	              onClick={() => saveMutation.mutate()}
	            >
	              {saveMutation.isLoading
	                ? 'Saving...'
	                : quoteId
	                ? 'Save Quote'
	                : 'Create Quote'}
	            </button>
	          ) : null}
	        </>
	      }
    >
      {isLoading ? (
        <LoadingSpinner className="py-20" />
      ) : (
        <div className="space-y-5">
          {publicUrl ? (
            <div className="rounded-card border border-primary-200 bg-primary-50 p-3 text-sm text-on-surface">
              Public link:{' '}
              <a
                className="font-medium text-primary-700 underline"
                href={publicUrl}
                target="_blank"
                rel="noreferrer"
              >
                {publicUrl}
              </a>
            </div>
          ) : null}

          <div className="grid min-w-0 gap-5 xl:grid-cols-[minmax(0,1fr)_320px]">
            <aside className="order-first space-y-4 xl:order-last xl:sticky xl:top-0 xl:self-start">
              <div className="sticky top-0 z-20 rounded-card border border-outline/70 bg-surface-container-lowest p-3 shadow-ambient xl:static xl:p-4">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div>
                    <h3 className="text-sm font-semibold text-on-surface">
                      Pricing Preview
                    </h3>
                    <p className="mt-1 text-xs text-on-surface-variant">
                      Stays visible while editing quote details.
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-on-surface-variant">Total</p>
                    <p className="text-lg font-semibold text-on-surface">
                      {pricingTotal}
                    </p>
                  </div>
                </div>
                <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 text-sm xl:block xl:space-y-2">
                  <div className="flex justify-between gap-3">
                    <dt className="text-on-surface-variant">Subtotal</dt>
                    <dd>{formatMoney(previewPricing?.subtotal || quote?.subtotal)}</dd>
                  </div>
                  <div className="flex justify-between gap-3">
                    <dt className="text-on-surface-variant">Service</dt>
                    <dd>
                      {formatMoney(
                        previewPricing?.serviceCharge || quote?.serviceChargeAmount
                      )}
                    </dd>
                  </div>
                  <div className="flex justify-between gap-3">
                    <dt className="text-on-surface-variant">Gratuity</dt>
                    <dd>
                      {formatMoney(previewPricing?.gratuity || quote?.gratuityAmount)}
                    </dd>
                  </div>
                  <div className="flex justify-between gap-3">
                    <dt className="text-on-surface-variant">Tax</dt>
                    <dd>{formatMoney(previewPricing?.tax || quote?.taxAmount)}</dd>
                  </div>
                  <div className="flex justify-between gap-3 border-t border-outline/60 pt-2 font-semibold">
                    <dt>Total</dt>
                    <dd>{pricingTotal}</dd>
                  </div>
	                  <div className="flex justify-between gap-3">
	                    <dt className="text-on-surface-variant">Deposit</dt>
	                    <dd>{pricingDeposit}</dd>
	                  </div>
	                </dl>
	                {quoteId ? (
	                  <div className="mt-4 rounded-card border border-outline/60 bg-surface-container-low p-3">
	                    <div className="flex items-start justify-between gap-3">
	                      <div>
	                        <p className="text-xs font-semibold uppercase text-on-surface-variant">
	                          Readiness
	                        </p>
	                        <p className="mt-1 text-sm font-semibold text-on-surface">
	                          {readiness.blockingIssues.length
	                            ? `${readiness.blockingIssues.length} issue${
	                                readiness.blockingIssues.length === 1
	                                  ? ''
	                                  : 's'
	                              } to fix`
	                            : 'Ready for customer action'}
	                        </p>
	                      </div>
	                      <span
	                        className={`rounded-pill px-2 py-1 text-xs font-semibold ${
	                          readiness.blockingIssues.length
	                            ? 'bg-red-100 text-red-800'
	                            : 'bg-green-100 text-green-800'
	                        }`}
	                      >
	                        {readiness.blockingIssues.length ? 'Blocked' : 'Ready'}
	                      </span>
	                    </div>
	                    {readiness.blockingIssues.length ? (
	                      <ul className="mt-3 space-y-1 text-xs leading-5 text-red-700">
	                        {readiness.blockingIssues.map((issue: string) => (
	                          <li key={issue}>{issue}</li>
	                        ))}
	                      </ul>
	                    ) : null}
	                    {readiness.warnings.length ? (
	                      <ul className="mt-3 space-y-1 text-xs leading-5 text-yellow-800">
	                        {readiness.warnings.map((warning: string) => (
	                          <li key={warning}>{warning}</li>
	                        ))}
	                      </ul>
	                    ) : null}
	                  </div>
	                ) : null}
	                <div className="mt-4 rounded-card border border-outline/60 bg-surface-container-low p-3">
	                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div>
                      <p className="text-xs font-semibold uppercase text-on-surface-variant">
                        Deposit Payment
                      </p>
                      <p className="mt-1 text-sm font-semibold text-on-surface">
                        {getDepositStatusLabel(quote)}
                      </p>
                    </div>
                    <span
                      className={`rounded-pill px-2 py-1 text-xs font-semibold ${depositStatusClass(
                        quote
                      )}`}
                    >
                      {formatMoney(quote?.depositDue)}
                    </span>
                  </div>
                  <dl className="mt-3 space-y-2 text-xs">
                    {depositPaymentRequestId ? (
                      <div className="flex justify-between gap-3">
                        <dt className="text-on-surface-variant">Request</dt>
                        <dd className="break-all text-right text-on-surface">
                          {depositPaymentRequestId.slice(0, 8)}
                        </dd>
                      </div>
                    ) : null}
                    {depositInvoiceNumber ? (
                      <div className="flex justify-between gap-3">
                        <dt className="text-on-surface-variant">Invoice</dt>
                        <dd className="text-right text-on-surface">
                          {depositInvoiceNumber}
                        </dd>
                      </div>
                    ) : null}
                    {depositPaidAt ? (
                      <div className="flex justify-between gap-3">
                        <dt className="text-on-surface-variant">Paid</dt>
                        <dd className="text-right text-on-surface">
                          {formatDate(depositPaidAt)}
                        </dd>
                      </div>
                    ) : null}
                  </dl>
                  {depositPaymentUrl ? (
                    <div className="mt-3 flex flex-wrap gap-2">
                      <button
                        type="button"
                        className="btn-secondary px-3 py-2 text-xs"
                        onClick={copyDepositPaymentLink}
                      >
                        Copy payment link
                      </button>
                      <a
                        className="btn-secondary px-3 py-2 text-xs"
                        href={depositPaymentUrl}
                        target="_blank"
                        rel="noreferrer"
                      >
                        Open
                      </a>
                    </div>
                  ) : null}
                </div>

                <div className="mt-4 rounded-card border border-outline/60 bg-surface-container-low p-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <p className="text-xs font-semibold uppercase text-on-surface-variant">
                        Charges
                      </p>
                      <p className="mt-1 text-xs text-on-surface-variant">
                        Saved into this quote's terms snapshot.
                      </p>
                    </div>
                    <button
                      type="button"
                      className="admin-action-pill rounded-md bg-surface-container-lowest px-2 py-1 text-xs font-medium text-on-surface"
                      onClick={() =>
                        patchQuoteTerms({
                          service_charge_percent: 0,
                          gratuity_percent: 20,
                          tax_rate: 0,
                        })
                      }
                    >
                      Standard
                    </button>
                  </div>
                  <div className="mt-3 grid grid-cols-3 gap-2">
                    <label className="min-w-0">
                      <span className="text-xs font-medium text-on-surface-variant">
                        Service %
                      </span>
                      <input
                        className="input-field mt-1 px-2 text-sm"
                        type="number"
                        min="0"
                        step="0.01"
                        value={effectiveQuoteTerms.service_charge_percent ?? 0}
                        onChange={(event) =>
                          patchQuoteTerms({
                            service_charge_percent: Number(event.target.value),
                          })
                        }
                      />
                    </label>
                    <label className="min-w-0">
                      <span className="text-xs font-medium text-on-surface-variant">
                        Grat. %
                      </span>
                      <input
                        className="input-field mt-1 px-2 text-sm"
                        type="number"
                        min="0"
                        step="0.01"
                        value={effectiveQuoteTerms.gratuity_percent ?? 0}
                        onChange={(event) =>
                          patchQuoteTerms({
                            gratuity_percent: Number(event.target.value),
                          })
                        }
                      />
                    </label>
                    <label className="min-w-0">
                      <span className="text-xs font-medium text-on-surface-variant">
                        Tax %
                      </span>
                      <input
                        className="input-field mt-1 px-2 text-sm"
                        type="number"
                        min="0"
                        step="0.01"
                        value={Number(
                          ((effectiveQuoteTerms.tax_rate || 0) * 100).toFixed(4)
                        )}
                        onChange={(event) =>
                          patchQuoteTerms({
                            tax_rate: Number(event.target.value) / 100,
                          })
                        }
                      />
                    </label>
                  </div>
                </div>
	                {quoteId && canUseAction('recalculate') ? (
	                  <button
	                    type="button"
	                    className="btn-secondary mt-4 w-full"
	                    disabled={actionMutation.isLoading}
	                    onClick={() => actionMutation.mutate('recalculate')}
	                  >
                    <ArrowPathIcon className="mr-1 inline h-4 w-4" />
                    Recalculate
                  </button>
	                ) : null}
	              </div>
	
	              {quoteId ? (
	                <div className="rounded-card border border-outline/70 bg-surface-container-lowest p-4">
	                  <h3 className="text-sm font-semibold text-on-surface">
	                    Delivery
	                  </h3>
	                  <dl className="mt-3 space-y-2 text-xs">
	                    <div className="flex justify-between gap-3">
	                      <dt className="text-on-surface-variant">Public link</dt>
	                      <dd className="text-right text-on-surface">
	                        {quote?.hasPublicLink ? 'Active' : 'Not active'}
	                      </dd>
	                    </div>
	                    {quote?.viewedAt ? (
	                      <div className="flex justify-between gap-3">
	                        <dt className="text-on-surface-variant">Viewed</dt>
	                        <dd className="text-right text-on-surface">
	                          {formatDate(quote.viewedAt)}
	                        </dd>
	                      </div>
	                    ) : null}
	                    {lastDelivery ? (
	                      <>
	                        <div className="flex justify-between gap-3">
	                          <dt className="text-on-surface-variant">Last send</dt>
	                          <dd
	                            className={
	                              lastDelivery.success
	                                ? 'text-right text-green-700'
	                                : 'text-right text-red-700'
	                            }
	                          >
	                            {lastDelivery.success ? 'Delivered' : 'Failed'}
	                          </dd>
	                        </div>
	                        {lastDelivery.error ? (
	                          <div className="rounded-card border border-red-200 bg-red-50 p-2 text-xs text-red-700">
	                            {lastDelivery.error}
	                          </div>
	                        ) : null}
	                      </>
	                    ) : null}
	                  </dl>
	                </div>
	              ) : null}
	
	              {quoteId ? (
	                <div className="rounded-card border border-outline/70 bg-surface-container-lowest p-4">
	                  <h3 className="text-sm font-semibold text-on-surface">
	                    Handoff
	                  </h3>
	                  {quote?.convertedReservationId ? (
	                    <div className="mt-3 space-y-2 text-sm">
	                      <Link
	                        className="btn-secondary w-full justify-center"
	                        to={convertedReservationAdminUrl || '/admin/reservations'}
	                      >
	                        <ArrowTopRightOnSquareIcon className="mr-1 inline h-4 w-4" />
	                        Open Reservation
	                      </Link>
	                      {convertedReservationPublicUrl ? (
	                        <div className="flex gap-2">
	                          <button
	                            type="button"
	                            className="btn-secondary flex-1 px-3 py-2 text-xs"
	                            onClick={copyReservationRecordLink}
	                          >
	                            Copy Record
	                          </button>
	                          <a
	                            className="btn-secondary flex-1 px-3 py-2 text-center text-xs"
	                            href={convertedReservationPublicUrl}
	                            target="_blank"
	                            rel="noreferrer"
	                          >
	                            View Record
	                          </a>
	                        </div>
	                      ) : null}
	                    </div>
	                  ) : (
	                    <p className="mt-2 text-sm leading-6 text-on-surface-variant">
	                      Reservation handoff appears after customer acceptance and
	                      conversion.
	                    </p>
	                  )}
	                </div>
	              ) : null}

	              {quote?.termsSnapshot ? (
                <div className="rounded-card border border-outline/70 bg-surface-container-lowest p-4">
                  <h3 className="text-sm font-semibold text-on-surface">
                    Effective Terms
                  </h3>
                  <div className="mt-3">
                    <QuoteTermsSummary compact terms={quote.termsSnapshot} />
                  </div>
                </div>
              ) : null}
            </aside>

            <div className="min-w-0 space-y-3">
              {quoteId ? <label className="block text-xs font-medium">Change reason{quote?.sentAt || locked ? ' (required)' : ''}<textarea className="input-field mt-1 min-h-[72px]" maxLength={1000} value={changeReason} onChange={(event) => setChangeReason(event.target.value)} /></label> : null}
              {locked ? <p className="text-sm text-on-surface-variant">Commercial terms require an amendment.</p> : null}
              <TripDetailsEditor value={{ ...(quote?.tripDetails || {}), ...tripPatch }} onChange={(patch) => setTripPatch((current) => ({ ...current, ...patch }))} commercialLocked={locked} />
              <fieldset disabled={locked} className="min-w-0 space-y-3 disabled:opacity-60">
              <QuoteAccordionSection
                title="Customer and Account"
                description="Connect the quote to the paying account and customer contact."
                summary={customerSummary}
              >
                <div className="grid min-w-0 gap-4 md:grid-cols-2">
                  <AccountTypeahead
                    value={selectedAccount}
                    onChange={(option) => {
                      setSelectedAccount(option);
                      setForm((current) => ({
                        ...current,
                        accountId: option?.id || null,
                      }));
                    }}
                    allowCreate
                  />
                  <CustomerTypeahead
                    value={selectedCustomer}
                    onChange={(option) => {
                      setSelectedCustomer(option);
                      setForm((current) => ({
                        ...current,
                        localCustomerId: option?.id || null,
                        customerReference: option?.id || null,
                        customerName: option?.label || current.customerName,
                        customerEmail:
                          option?.email && option.email !== 'No email'
                            ? option.email
                            : current.customerEmail,
                      }));
                    }}
                  />
                  <label className="min-w-0">
                    <span className="text-xs font-medium text-on-surface-variant">
                      Customer name
                    </span>
                    <input
                      className="input-field mt-1"
                      value={form.customerName}
                      onChange={(event) =>
                        setForm({ ...form, customerName: event.target.value })
                      }
                    />
                  </label>
                  <label className="min-w-0">
                    <span className="text-xs font-medium text-on-surface-variant">
                      Customer email
                    </span>
                    <input
                      className="input-field mt-1"
                      type="email"
                      value={form.customerEmail}
                      onChange={(event) =>
                        setForm({ ...form, customerEmail: event.target.value })
                      }
                    />
                  </label>
                </div>
              </QuoteAccordionSection>

              <QuoteAccordionSection
                title="Event Details"
                description="Classify the work and set the service timing."
                summary={eventSummary || 'Event timing not set'}
              >
                <div className="grid min-w-0 gap-4 sm:grid-cols-2 xl:grid-cols-3">
                  <label className="min-w-0">
                    <span className="text-xs font-medium text-on-surface-variant">
                      Event type
                    </span>
                    <select
                      className="input-field mt-1"
                      value={form.eventType}
                      onChange={(event) =>
                        setForm({ ...form, eventType: event.target.value })
                      }
                    >
                      {EVENT_TYPES.map((eventType) => (
                        <option key={eventType} value={eventType}>
                          {labelize(eventType)}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="min-w-0">
                    <span className="text-xs font-medium text-on-surface-variant">
                      Event name
                    </span>
                    <input
                      className="input-field mt-1"
                      value={form.eventName}
                      onChange={(event) =>
                        setForm({ ...form, eventName: event.target.value })
                      }
                    />
                  </label>
                  <label className="min-w-0">
                    <span className="text-xs font-medium text-on-surface-variant">
                      Event date
                    </span>
                    <input
                      className="input-field mt-1"
                      type="datetime-local"
                      value={form.eventDate}
                      onChange={(event) =>
                        setForm({ ...form, eventDate: event.target.value })
                      }
                    />
                  </label>
                  <label className="min-w-0">
                    <span className="text-xs font-medium text-on-surface-variant">
                      Service start
                    </span>
                    <input
                      className="input-field mt-1"
                      type="datetime-local"
                      value={form.startDate}
                      onChange={(event) =>
                        setForm({ ...form, startDate: event.target.value })
                      }
                    />
                  </label>
                  <label className="min-w-0">
                    <span className="text-xs font-medium text-on-surface-variant">
                      Service end
                    </span>
                    <input
                      className="input-field mt-1"
                      type="datetime-local"
                      value={form.endDate}
                      onChange={(event) =>
                        setForm({ ...form, endDate: event.target.value })
                      }
                    />
                  </label>
                </div>
              </QuoteAccordionSection>

              <QuoteAccordionSection
                title="Itinerary"
                description="Exact pickup, stop, and dropoff addresses for the customer quote."
                summary={`${pickupSummary} → ${dropoffSummary}`}
                actions={
                  <button
                    type="button"
                    className="btn-secondary text-xs"
                    onClick={() =>
                      setForm({ ...form, stops: [...form.stops, emptyStop()] })
                    }
                  >
                    Add Stop
                  </button>
                }
              >
                <div className="space-y-3">
                  <div className="rounded-card border border-outline/60 bg-surface-container-low p-3 sm:p-4">
                    <div className="mb-3 min-w-0">
                      <h4 className="text-sm font-semibold text-on-surface">
                        Pickup Address
                      </h4>
                      <p className="mt-1 truncate text-xs text-on-surface-variant">
                        {pickupSummary}
                      </p>
                    </div>
                    <AddressFields
                      prefix="Pickup"
                      address={form.pickupAddress}
                      onChange={(patch) =>
                        updateAddress('pickupAddress', patch)
                      }
                    />
                  </div>

                  {form.stops.map((stop, index) => {
                    const stopSummary =
                      formatAddress(stop) ||
                      `Stop ${index + 1} address not set`;

                    return (
                      <div
                        key={stop.id}
                        className="rounded-card border border-outline/60 bg-surface-container-low p-3 sm:p-4"
                      >
                        <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
                          <div className="min-w-0 flex-1">
                            <h4 className="text-sm font-semibold text-on-surface">
                              Stop {index + 1}
                            </h4>
                            <p className="mt-1 truncate text-xs text-on-surface-variant">
                              {stopSummary}
                            </p>
                          </div>
                          <button
                            type="button"
                            className="btn-secondary text-xs"
                            onClick={() =>
                              setForm({
                                ...form,
                                stops: form.stops.filter(
                                  (entry) => entry.id !== stop.id
                                ),
                              })
                            }
                          >
                            Remove Stop
                          </button>
                        </div>
                        <AddressFields
                          prefix={`Stop ${index + 1}`}
                          address={stop}
                          onChange={(patch) => updateStop(stop.id, patch)}
                        />
                      </div>
                    );
                  })}

                  <div className="rounded-card border border-outline/60 bg-surface-container-low p-3 sm:p-4">
                    <div className="mb-3 min-w-0">
                      <h4 className="text-sm font-semibold text-on-surface">
                        Dropoff Address
                      </h4>
                      <p className="mt-1 truncate text-xs text-on-surface-variant">
                        {dropoffSummary}
                      </p>
                    </div>
                    <AddressFields
                      prefix="Dropoff"
                      address={form.dropoffAddress}
                      onChange={(patch) =>
                        updateAddress('dropoffAddress', patch)
                      }
                    />
                  </div>
                </div>
              </QuoteAccordionSection>

              <QuoteAccordionSection
                title="Vehicle Requirements"
                description="Quote by vehicle type and quantity; exact assignment happens later."
                summary={`${form.vehicleRequirements.length} requirement${
                  form.vehicleRequirements.length === 1 ? '' : 's'
                }`}
                actions={
                  <button
                    type="button"
                    className="btn-secondary text-xs"
                    onClick={() =>
                      setForm({
                        ...form,
                        vehicleRequirements: [
                          ...form.vehicleRequirements,
                          emptyRequirement(),
                        ],
                      })
                    }
                  >
                    Add Requirement
                  </button>
                }
              >
                <div className="space-y-3">
	                  {form.vehicleRequirements.map((requirement, index) => {
	                    const savedRequirement = Array.isArray(
	                      quote?.vehicleRequirements
	                    )
	                      ? quote.vehicleRequirements[index]
	                      : null;
                    const category =
                      vehicleCategoryPricing[
                        String(requirement.vehicleType || '').toLowerCase()
                      ];
                    const categoryUnitRate = getCategoryUnitRate(
                      category,
                      requirement.rateType
                    );
	                    const isUsingCategoryPricing =
	                      requirement.unitRate === '' ||
	                      requirement.unitRate == null;
	                    const pricingSourceText =
	                      savedRequirement?.pricingSourceSummary ||
	                      savedRequirement?.pricing_source_summary ||
	                      null;

	                    return (
                      <div
                        key={`${requirement.id || index}`}
                        className="rounded-card border border-outline/60 bg-surface-container-low p-3 sm:p-4"
                      >
                        <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
                          <div>
                            <h4 className="text-sm font-semibold text-on-surface">
                              Requirement {index + 1}
                            </h4>
                            <p className="mt-1 text-xs text-on-surface-variant">
                              {labelize(requirement.vehicleType)} · Qty{' '}
                              {requirement.quantity || 1}
                            </p>
                          </div>
                          <button
                            type="button"
                            className="btn-secondary text-xs"
                            disabled={form.vehicleRequirements.length === 1}
                            onClick={() =>
                              setForm({
                                ...form,
                                vehicleRequirements:
                                  form.vehicleRequirements.filter(
                                    (_entry, i) => i !== index
                                  ),
                              })
                            }
                          >
                            Remove
                          </button>
                        </div>
                        <div className="grid min-w-0 gap-3 sm:grid-cols-2 xl:grid-cols-3">
                          <label className="min-w-0">
                            <span className="text-xs font-medium text-on-surface-variant">
                              Type
                            </span>
                            <select
                              className="input-field mt-1"
                              value={requirement.vehicleType}
                              onChange={(event) =>
                                updateRequirement(index, {
                                  vehicleType: event.target.value,
                                })
                              }
                            >
                              {VEHICLE_TYPES.map((vehicleType) => (
                                <option key={vehicleType} value={vehicleType}>
                                  {labelize(vehicleType)}
                                </option>
                              ))}
                            </select>
                          </label>
                          <label className="min-w-0">
                            <span className="text-xs font-medium text-on-surface-variant">
                              Label
                            </span>
                            <input
                              className="input-field mt-1"
                              value={requirement.vehicleLabel || ''}
                              onChange={(event) =>
                                updateRequirement(index, {
                                  vehicleLabel: event.target.value,
                                })
                              }
                              placeholder="Motorcoach, Sprinter..."
                            />
                          </label>
                          <label className="min-w-0">
                            <span className="text-xs font-medium text-on-surface-variant">
                              Qty
                            </span>
                            <input
                              className="input-field mt-1"
                              type="number"
                              min="1"
                              value={requirement.quantity}
                              onChange={(event) =>
                                updateRequirement(index, {
                                  quantity: Number(event.target.value),
                                })
                              }
                            />
                          </label>
                          <label className="min-w-0">
                            <span className="text-xs font-medium text-on-surface-variant">
                              Rate
                            </span>
                            <select
                              className="input-field mt-1"
                              value={requirement.rateType}
                              onChange={(event) =>
                                updateRequirement(index, {
                                  rateType: event.target.value as any,
                                })
                              }
                            >
                              <option value="hourly">Hourly</option>
                              <option value="daily">Daily</option>
                              <option value="fixed">Fixed</option>
                            </select>
                          </label>
                          <label className="min-w-0">
                            <span className="text-xs font-medium text-on-surface-variant">
                              Hours
                            </span>
                            <input
                              className="input-field mt-1"
                              type="number"
                              min="0"
                              value={requirement.hours}
                              onChange={(event) =>
                                updateRequirement(index, {
                                  hours:
                                    event.target.value === ''
                                      ? ''
                                      : Number(event.target.value),
                                })
                              }
                            />
                          </label>
                          <label className="min-w-0">
                            <span className="text-xs font-medium text-on-surface-variant">
                              Unit rate
                            </span>
                            <input
                              className="input-field mt-1"
                              type="number"
                              min="0"
                              step="0.01"
                              value={requirement.unitRate}
                              onChange={(event) =>
                                updateRequirement(index, {
                                  unitRate:
                                    event.target.value === ''
                                      ? ''
                                      : Number(event.target.value),
                                })
                              }
                            />
                          </label>
                        </div>
	                        <div className="mt-4 flex flex-col gap-2 rounded-card border border-outline/60 bg-surface-container-lowest px-3 py-2 text-xs text-on-surface-variant sm:flex-row sm:items-center sm:justify-between">
	                          <span className="min-w-0 break-words">
	                            {pricingSourceText
	                              ? pricingSourceText
	                              : isUsingCategoryPricing
	                              ? `Using ${labelize(
	                                  requirement.vehicleType
	                                )} category pricing: ${formatRate(
                                  categoryUnitRate || null,
                                  requirement.rateType
                                )}`
                              : `Manual override: ${formatRate(
                                  requirement.unitRate,
                                  requirement.rateType
                                )}`}
                            {category
                              ? ` · ${category.availableCount}/${category.totalCount} available`
                              : ' · tenant rate card fallback if no fleet rate exists'}
                          </span>
                          {categoryUnitRate > 0 ? (
                            <button
                              type="button"
                              className="admin-action-pill flex-shrink-0 rounded-md bg-surface-container-low px-2 py-1 font-medium text-on-surface"
                              onClick={() =>
                                updateRequirement(index, {
                                  unitRate: categoryUnitRate,
                                })
                              }
                            >
                              Lock Category Rate
                            </button>
                          ) : null}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </QuoteAccordionSection>

              <QuoteAccordionSection
                title="Terms Snapshot"
                description="Override account or event terms only when this quote needs special handling."
                summary="Deposits, cancellation, minimums, fees, and acceptance requirements."
                defaultOpen={false}
              >
                <QuoteTermsEditor
                  value={form.termsOverride}
                  onChange={(next) => setForm({ ...form, termsOverride: next })}
                  inheritanceMode
                />
              </QuoteAccordionSection>
              </fieldset>
            </div>
          </div>
        </div>
      )}
    </EntityDrawer>
  );
}
